import React, {ReactElement, useState} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton
} from '@material-ui/core';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from 'react-hook-form';
import {useSnackbar} from 'notistack';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import './SharePaymentDialog.scss';
import {multiEmailValidationWithHookForm} from '@shared/modules/email/Email';
import CheckBoxField from '@components/system/CheckBox/CheckBoxField';
import './../../DueInvoices/DueInvoicesStyles.scss';
import PhoneInputField from '@components/system/PhoneInputField/PhoneInputField';
import {sendReminderToGuarantor} from '@shared/services/DueInvoiceServices';
import {generateThePaymentToken} from '@shared/services/DueInvoiceServices';
import HelperUtils from '@shared/utils/HelperUtils';
import NavRoutes from 'src/constants/route/NavRoutes';
import FileCopy from '@material-ui/icons/FileCopy';
import {validateNotificationPhoneHookForm} from '@shared/modules/invoice/Invoice';
import {NotificationType} from '@shared/constants/Mapping';
// interface for Confirmation Dialog props
interface SendPaymentRecieptDialogProps {
  open: boolean;
  title: string | React.ReactNode;
  subtitle: string | React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  onClose(): void;
  selectedGuarantor: any;
  groupId: string | number;
  clinicId?: string | number;
  copyText?: string;
}

const SharePaymentDialog = (
  props: SendPaymentRecieptDialogProps
): ReactElement => {
  const {
    open,
    onClose,
    title,
    subtitle,
    selectedGuarantor,
    groupId,
    copyText,
    clinicId = ''
  } = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [loader, setLoader] = React.useState(false);
  // const [mail, setMail] = React.useState('');
  const [reminderType, setReminderType] = useState<Array<string>>([]);
  const [url, setUrl] = useState<string>(copyText || '');
  const [isCopied, setIsCopied] = useState(false);
  const submitButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const getPhoneNumber = (phone: string): string => {
    let phoneNumber = phone;
    if (!phoneNumber) {
      return '';
    }
    if (phoneNumber.substr(0, 1) != '+') {
      phoneNumber = '+1' + phoneNumber;
    }
    return phoneNumber;
  };
  const {
    control,
    getValues,
    handleSubmit,
    errors,
    formState
    // setValue
  } = useForm<{
    email: string;
    notificationPhone: string;
  }>({
    mode: 'all',
    defaultValues: {
      email: selectedGuarantor.email,
      notificationPhone: getPhoneNumber(selectedGuarantor.notification_phone)
    },
    resolver: async values => {
      let error = {};
      if (reminderType.includes(NotificationType.EMAIL)) {
        error = Object.assign({}, multiEmailValidationWithHookForm(values));
      }
      if (reminderType.includes(NotificationType.SMS)) {
        error = Object.assign(error, validateNotificationPhoneHookForm(values));
      }
      return {
        values,
        errors: error
      };
    }
  });

  const checkFormState = (): void => {
    const {isValid} = formState;
    if (!isValid) {
      enqueueSnackbar(
        t('VALIDATORS.REQUIRED_FORM_FIELD_MESSAGE'),
        SnackBarConfig('w')
      );
    }
  };

  const handleClose = React.useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);
  // React.useEffect(() => {
  //   setTimeout(() => {
  //     setValue('email', selectedGuarantor.email);
  //   }, 250);
  // }, [selectedGuarantor, setValue]);

  React.useEffect(() => {
    if (selectedGuarantor) {
      if (selectedGuarantor?.payment_token) {
        setUrl(
          HelperUtils.generateRouteURL({
            path: `/${NavRoutes.PUBLIC.PAY}/${selectedGuarantor.payment_token}`
          })
        );
        setLoader(false);
      } else {
        generateThePaymentToken(groupId, selectedGuarantor?.id)
          .then(res => {
            setUrl(
              HelperUtils.generateRouteURL({
                path: `/${NavRoutes.PUBLIC.PAY}/${res.data.payment_token}`
              })
            );
            setLoader(false);
          })
          .catch(err => {
            enqueueSnackbar(err.message, SnackBarConfig('e'));
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, selectedGuarantor]);

  const handleSendNotificationEmailButtonAction = async (
    data: any
  ): Promise<void> => {
    try {
      setLoader(true);
      const email: string = getValues('email');
      const phone: string = getValues('notificationPhone');
      const reminderStatus = await sendReminderToGuarantor(
        groupId,
        clinicId,
        data.id,
        {
          email,
          phone,
          reminder_type: reminderType.join(',')
        }
      );
      if (reminderStatus) {
        enqueueSnackbar(t('DUE_INVOICES.MESSAGE_SENT'), SnackBarConfig('i'));
        handleClose();
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
    } finally {
      setLoader(false);
    }
  };
  const handleSmsCheckBox = (reminderType_: string) => {
    if (reminderType.includes(reminderType_)) {
      // uncheck
      setReminderType(
        reminderType.filter((item: string) => item !== reminderType_)
      );
    } else {
      setReminderType([...reminderType, reminderType_]);
    }
  };
  const PhoneNumberField = () => {
    return (
      <div className="main_view">
        <div className="main_view">
          <div style={{marginTop: '0.9rem'}}>
            <CheckBoxField
              data-test-id="pcl-statements-mobileCheckbox"
              className="checkbox-view"
              id={'mobile-checkbox'}
              checked={reminderType.includes(NotificationType.SMS)}
              onChange={() => handleSmsCheckBox(NotificationType.SMS)}
            />
          </div>
          <label htmlFor="mobile-checkbox" className="email_span_tag">
            {t('DUE_INVOICES.PLACEHOLDERS.SMS')}
          </label>
        </div>
        <div className="form" data-test-id="pcl-statements-phoneInputBox">
          <FormControl
            style={{paddingLeft: '0.5rem'}}
            error={!!errors.notificationPhone}
            fullWidth={true}
            variant="outlined">
            <Controller
              id="notificationPhone"
              render={({value, onBlur, onChange}) => (
                <PhoneInputField
                  value={value}
                  onBlur={onBlur}
                  inputProps={{
                    name: 'notificationPhone',
                    id: 'notificationPhone'
                  }}
                  disableCountryCode={false}
                  disableCountryGuess={false}
                  country={'us'}
                  onlyCountries={undefined}
                  onChange={(val, country, event) => {
                    event.persist();
                    onChange(event);
                  }}
                />
              )}
              margin={'dense'}
              control={control}
              name={'notificationPhone'}
              type={'text'}
              placeholder={t('FORM.PLACEHOLDERS.NOTIFICATION_PHONE')}
              defaultValue=""
              error={!!errors.notificationPhone}
            />
            {errors.notificationPhone && (
              <FormHelperText>
                {t((errors.notificationPhone as unknown) as string)}
              </FormHelperText>
            )}
          </FormControl>
        </div>
      </div>
    );
  };
  const EmailField = () => {
    return (
      <div className="main_view">
        <div className="main_view">
          <div style={{marginTop: '0.9rem'}}>
            <CheckBoxField
              data-test-id="pcl-statements-mailCheckbox"
              id={'checkbox-mail'}
              checked={reminderType.includes('email')}
              onChange={() => handleSmsCheckBox('email')}
            />
          </div>
          <label htmlFor="checkbox-mail" className="email_span_tag">
            {t('DUE_INVOICES.PLACEHOLDERS.EMAIL')}
          </label>
        </div>
        <div className="form">
          <FormControl
            error={!!errors.email}
            fullWidth={true}
            variant="outlined">
            <Controller
              style={{width: '100%'}}
              id="outlined-adornment-email"
              as={StyledOutlinedInput}
              margin={'dense'}
              control={control}
              name={'email'}
              type={'text'}
              autoComplete="on"
              placeholder={t('FORM.PLACEHOLDERS.EMAIL')}
              labelWidth={0}
              defaultValue=""
            />
            {errors?.email && errors?.email.message && (
              <FormHelperText>{t(errors.email?.message)}</FormHelperText>
            )}
          </FormControl>
        </div>
      </div>
    );
  };
  async function copyTextToClipboard(text: string) {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }
  const handleCopyClick = () => {
    copyTextToClipboard(url)
      .then(() => {
        // If successful, update the isCopied state value
        setIsCopied(true);
        enqueueSnackbar(t('COMMON.COPY_SUCCESS'), SnackBarConfig('i'));
        setTimeout(() => {
          setIsCopied(false);
        }, 1500);
      })
      .catch(err => {
        throw err;
      });
  };
  return (
    <Dialog
      maxWidth={'md'}
      open={open}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      onClose={handleClose}
      className="share-payment-dialog"
      aria-labelledby="confirm-dialog"
      aria-describedby="confirm-dialog">
      <DialogTitle disableTypography>{title}</DialogTitle>
      <form
        onSubmit={handleSubmit(() => {
          setLoader(true);
          handleSendNotificationEmailButtonAction(selectedGuarantor);
        })}>
        <DialogContent dividers>
          <div className="main_view">
            <div style={{marginRight: '0.7rem'}}>
              <div className="main_view margin_bottom">
                <div>{t('PAYMENT_HISTORY.EMAIL_DIALOG.PAYMENT_URL')}</div>
                <div className="url-style">{url}</div>
              </div>
              <div className="margin_bottom">{subtitle}</div>
              {PhoneNumberField()}
              {EmailField()}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          {/* <div> */}
          <div className="copy-button" data-test-id="pcl-statements-copyLink">
            <IconButton
              id="copy-button-id"
              data-test-id="pcl-payments-copyPaymentLink"
              color="primary"
              aria-label="toggle password visibility"
              onClick={handleCopyClick}
              edge="start"
              className="copy-icon-wrapper">
              {<FileCopy color={isCopied ? 'primary' : undefined} />}
            </IconButton>
            <label htmlFor="copy-button-id" className="label-conatiner">
              {t('COMMON.COPY')}
            </label>
          </div>
          <div>
            <Button
              data-test-id="pcl-statements-cancelButton"
              className="button-size"
              disabled={loader}
              onClick={() => handleClose()}
              variant="text">
              {t('PAYMENT_HISTORY.EMAIL_DIALOG.BUTTON_CANCEL')}
            </Button>
            <Button
              data-test-id="pcl-statements-sendButton"
              className="button-size"
              ref={submitButtonRef}
              disabled={!reminderType.length}
              variant="contained"
              onClick={checkFormState}
              type="submit"
              color="primary">
              {t('PAYMENT_HISTORY.EMAIL_DIALOG.BUTTON_SEND')}
            </Button>
          </div>
          {/* </div> */}
        </DialogActions>
      </form>
    </Dialog>
  );
};
export default SharePaymentDialog;
